import DownIcons from '@/icons/DownIcons'
import UpIcons from '@/icons/UpIcons'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface DisabledRange {
    start: string // Format: "HH:mm" (24-hour format)
    end: string // Format: "HH:mm" (24-hour format)
}

interface DurationPickerProps {
    value?: number // Duration in minutes
    onChange?: (value: number) => void
    placeholder?: string
    disabled?: boolean
    className?: string
    label?: string
    error?: string
    maxHours?: number // Maximum hours allowed (default: 23)
    showDays?: boolean // Whether to show days selection
    step?: number // Increment/decrement step in minutes (default: 15)
    showButtons?: boolean // Whether to show +/- buttons (default: true)
    currentTime?: string // Current time in "HH:mm" format
    disabledRanges?: DisabledRange[] // Array of disabled time ranges
}

const DurationPicker: React.FC<DurationPickerProps> = ({
    value = 0,
    onChange,
    placeholder = '0h 0m',
    disabled = false,
    className = '',
    error,
    maxHours = 23,
    showDays = false,
    step = 15, // Default 15 minutes increment
    showButtons = true,
    currentTime,
    disabledRanges = [],
}) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [selectedDays, setSelectedDays] = useState(0)
    const [selectedHours, setSelectedHours] = useState(0)
    const [selectedMinutes, setSelectedMinutes] = useState(0)
    const [isFocused, setIsFocused] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Helper function to convert time string to minutes since midnight
    const timeToMinutes = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
    }
    // Calculate maximum allowed duration based on current time and disabled ranges
    const calculateMaxAllowedDuration = (): number => {
        if (!currentTime || disabledRanges.length === 0) {
            return showDays ? 365 * 24 * 60 : maxHours * 60 + 59
        }

        const currentMinutes = timeToMinutes(currentTime)
        let maxDuration = showDays ? 365 * 24 * 60 : maxHours * 60 + 59

        // Find the earliest disabled range that would conflict
        for (const range of disabledRanges) {
            const rangeStartMinutes = timeToMinutes(range.start)
            const rangeEndMinutes = timeToMinutes(range.end)

            // If the disabled range starts after current time
            if (rangeStartMinutes > currentMinutes) {
                // Calculate duration until the start of this disabled range
                const durationUntilDisabled = rangeStartMinutes - currentMinutes
                maxDuration = Math.min(maxDuration, durationUntilDisabled)
            }
            // If current time is within a disabled range, set max duration to 0
            else if (
                currentMinutes >= rangeStartMinutes &&
                currentMinutes < rangeEndMinutes
            ) {
                maxDuration = 0
                break
            }
        }

        return maxDuration
    }

    // Parse initial value
    useEffect(() => {
        if (value >= 0) {
            const totalMinutes = Math.floor(value)
            const days = showDays ? Math.floor(totalMinutes / (24 * 60)) : 0
            const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
            const minutes = totalMinutes % 60

            setSelectedDays(days)
            setSelectedHours(hours)
            setSelectedMinutes(minutes)
            setInputValue(formatDuration(days, hours, minutes))
        }
    }, [value, showDays])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Format duration to string
    const formatDuration = (
        days: number,
        hours: number,
        minutes: number
    ): string => {
        const parts = []
        if (showDays && days > 0) parts.push(`${days}d`)
        if (hours > 0) parts.push(`${hours}h`)
        if (minutes > 0) parts.push(`${minutes}m`)
        return parts.length > 0 ? parts.join(' ') : '0m'
    }

    // Format duration with styled elements for display
    const formatDurationWithStyle = (
        days: number,
        hours: number,
        minutes: number
    ) => {
        const parts = []
        if (showDays && days > 0) {
            parts.push(
                <span key="days" className="text-gray-900">
                    {days}
                    <span className="text-gray-400">d</span>
                </span>
            )
        }
        if (hours > 0) {
            parts.push(
                <span key="hours" className="text-gray-900">
                    {hours}
                    <span className="text-gray-400">h</span>
                </span>
            )
        }
        if (minutes > 0) {
            parts.push(
                <span key="minutes" className="text-gray-900">
                    {minutes}
                    <span className="text-gray-400">m</span>
                </span>
            )
        }

        if (parts.length === 0) {
            return (
                <span className="text-gray-900">
                    0<span className="text-gray-400">m</span>
                </span>
            )
        }

        return parts.map((part, index) => (
            <React.Fragment key={index}>
                {index > 0 && <span className="mx-0.5"></span>}
                {part}
            </React.Fragment>
        ))
    }

    // Parse duration string
    const parseDuration = (
        str: string
    ): { days: number; hours: number; minutes: number } => {
        const dayMatch = str.match(/(\d+)d/)
        const hourMatch = str.match(/(\d+)h/)
        const minuteMatch = str.match(/(\d+)m/)

        return {
            days: dayMatch ? parseInt(dayMatch[1]) : 0,
            hours: hourMatch ? parseInt(hourMatch[1]) : 0,
            minutes: minuteMatch ? parseInt(minuteMatch[1]) : 0,
        }
    }

    // Calculate total minutes
    const calculateTotalMinutes = (
        days: number,
        hours: number,
        minutes: number
    ): number => {
        return days * 24 * 60 + hours * 60 + minutes
    }

    // Validate duration against constraints
    const validateDuration = (totalMinutes: number): number => {
        const maxAllowedDuration = calculateMaxAllowedDuration()
        return Math.min(totalMinutes, maxAllowedDuration)
    }

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputVal = e.target.value.toLowerCase()

        // Remove invalid characters, keep only digits, 'd', 'h', 'm', and spaces
        inputVal = inputVal.replace(/[^0-9dhm\s]/g, '')

        setInputValue(inputVal)

        // Try to parse and validate
        try {
            const parsed = parseDuration(inputVal)

            // Validate ranges
            if (showDays && parsed.days > 365) parsed.days = 365
            if (parsed.hours > (showDays ? 23 : maxHours)) {
                parsed.hours = showDays ? 23 : maxHours
            }
            if (parsed.minutes > 59) parsed.minutes = 59

            // Update if valid
            if (parsed.days >= 0 && parsed.hours >= 0 && parsed.minutes >= 0) {
                let totalMinutes = calculateTotalMinutes(
                    parsed.days,
                    parsed.hours,
                    parsed.minutes
                )

                const originalTotalMinutes = totalMinutes

                // Apply duration constraints based on current time and disabled ranges
                totalMinutes = validateDuration(totalMinutes)

                // Recalculate days, hours, minutes from constrained total
                const constrainedDays = showDays
                    ? Math.floor(totalMinutes / (24 * 60))
                    : 0
                const constrainedHours = Math.floor(
                    (totalMinutes % (24 * 60)) / 60
                )
                const constrainedMinutes = totalMinutes % 60

                setSelectedDays(constrainedDays)
                setSelectedHours(constrainedHours)
                setSelectedMinutes(constrainedMinutes)
                onChange?.(totalMinutes)

                // If the duration was constrained, update the input value immediately
                if (originalTotalMinutes !== totalMinutes) {
                    const constrainedFormatted = formatDuration(
                        constrainedDays,
                        constrainedHours,
                        constrainedMinutes
                    )
                    setInputValue(constrainedFormatted)
                }
            }
        } catch {
            // Invalid input, don't update
        }
    }

    // Handle input blur
    const handleInputBlur = () => {
        setIsFocused(false)
        // Reformat the input with current selected values
        const formatted = formatDuration(
            selectedDays,
            selectedHours,
            selectedMinutes
        )
        setInputValue(formatted)
    }

    // Handle input focus
    const handleInputFocus = () => {
        setIsFocused(true)
    }

    // Handle duration selection
    const handleDurationSelect = (
        days: number,
        hours: number,
        minutes: number
    ) => {
        let totalMinutes = calculateTotalMinutes(days, hours, minutes)

        // Apply duration constraints
        totalMinutes = validateDuration(totalMinutes)

        // Recalculate from constrained total
        const constrainedDays = showDays
            ? Math.floor(totalMinutes / (24 * 60))
            : 0
        const constrainedHours = Math.floor((totalMinutes % (24 * 60)) / 60)
        const constrainedMinutes = totalMinutes % 60

        setSelectedDays(constrainedDays)
        setSelectedHours(constrainedHours)
        setSelectedMinutes(constrainedMinutes)

        const formatted = formatDuration(
            constrainedDays,
            constrainedHours,
            constrainedMinutes
        )
        setInputValue(formatted)
        onChange?.(totalMinutes)
    }

    // Handle increment/decrement
    const handleIncrement = () => {
        const currentValue = value || 0
        const newValue = currentValue + step
        const finalValue = validateDuration(newValue)

        if (onChange) {
            onChange(finalValue)
        }
    }

    const handleDecrement = () => {
        const currentValue = value || 0
        const newValue = Math.max(0, currentValue - step)

        if (onChange) {
            onChange(newValue)
        }
    }

    // Generate options with constraints
    const maxAllowedDuration = calculateMaxAllowedDuration()
    const maxAllowedDays = showDays
        ? Math.floor(maxAllowedDuration / (24 * 60))
        : 0
    const maxAllowedHours = Math.floor((maxAllowedDuration % (24 * 60)) / 60)
    const maxAllowedMinutes = maxAllowedDuration % 60

    const dayOptions = showDays
        ? Array.from({ length: maxAllowedDays + 1 }, (_, i) => i)
        : [0]
    const hourOptions = Array.from(
        { length: Math.min(showDays ? 24 : maxHours + 1, maxAllowedHours + 1) },
        (_, i) => i
    )
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i).filter(
        (minute) => {
            // If we're at the maximum allowed hours, limit minutes
            if (
                selectedHours === maxAllowedHours &&
                selectedDays === maxAllowedDays
            ) {
                return minute <= maxAllowedMinutes
            }
            return true
        }
    )

    return (
        <div className={`relative ${className}`}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={handleInputFocus}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`
                        h-[36px] rounded-[4px]
                        w-full px-3 py-2 text-sm border
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        ${showButtons && !disabled ? 'pr-20' : 'pr-10'}
                        ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white'}
                        ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
                        ${!isFocused ? 'text-transparent caret-transparent' : 'text-gray-900'}
                    `}
                />

                {/* Styled display overlay - only show when not focused */}
                {!isFocused && (
                    <div
                        className={`
                        absolute inset-0 px-3 py-2 text-sm pointer-events-none
                        flex items-center
                        ${disabled ? 'text-gray-500' : 'text-gray-900'}
                    `}
                    >
                        {inputValue ? (
                            formatDurationWithStyle(
                                selectedDays,
                                selectedHours,
                                selectedMinutes
                            )
                        ) : (
                            <span className="text-gray-400">{placeholder}</span>
                        )}
                    </div>
                )}

                {/* Icons */}
                <div className="absolute inset-y-0 right-0 flex items-center flex-col h-full justify-center">
                    {showButtons && !disabled ? (
                        <>
                            <button
                                type="button"
                                onClick={handleIncrement}
                                disabled={disabled}
                                className={`pr-2 transition-colors h-1/2 ${value === maxAllowedDuration ? 'text-gray-400 cursor-not-allowed' : 'text-primary'}`}
                                title={`Tăng ${step} phút`}
                            >
                                <DownIcons className="w-4 h-3" />
                            </button>
                            <button
                                type="button"
                                onClick={handleDecrement}
                                disabled={disabled || value === 0}
                                className={`pr-2 transition-colors h-1/2 ${value === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-primary'} pb-3`}
                                title={`Giảm ${step} phút`}
                            >
                                <UpIcons className="w-4 h-3" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => !disabled && setIsOpen(!isOpen)}
                                disabled={disabled}
                                className={`p-2 text-gray-400 hover:text-gray-600 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <DownIcons className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* Dropdown */}
                {isOpen && !disabled && !showButtons && (
                    <div
                        ref={dropdownRef}
                        className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50"
                    >
                        <div
                            className={`flex max-h-60 ${showDays ? 'divide-x' : ''}`}
                        >
                            {/* Days */}
                            {showDays && (
                                <div className="flex-1 border-r border-gray-200">
                                    <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 text-center">
                                        {t('days') || 'Ngày'}
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {dayOptions.map((day) => (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() =>
                                                    handleDurationSelect(
                                                        day,
                                                        selectedHours,
                                                        selectedMinutes
                                                    )
                                                }
                                                className={`
                                                    w-full px-3 py-1 text-sm text-left hover:bg-blue-50
                                                    ${selectedDays === day ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700'}
                                                `}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Hours */}
                            <div className="flex-1 border-r border-gray-200">
                                <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 text-center">
                                    {t('hours') || 'Giờ'}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {hourOptions.map((hour) => (
                                        <button
                                            key={hour}
                                            type="button"
                                            onClick={() =>
                                                handleDurationSelect(
                                                    selectedDays,
                                                    hour,
                                                    selectedMinutes
                                                )
                                            }
                                            className={`
                                                w-full px-3 py-1 text-sm text-left hover:bg-blue-50
                                                ${selectedHours === hour ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700'}
                                            `}
                                        >
                                            {hour}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Minutes */}
                            <div className="flex-1">
                                <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 text-center">
                                    {t('minutes') || 'Phút'}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {minuteOptions.map((minute) => (
                                        <button
                                            key={minute}
                                            type="button"
                                            onClick={() =>
                                                handleDurationSelect(
                                                    selectedDays,
                                                    selectedHours,
                                                    minute
                                                )
                                            }
                                            className={`
                                                w-full px-3 py-1 text-sm text-left hover:bg-blue-50
                                                ${selectedMinutes === minute ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700'}
                                            `}
                                        >
                                            {minute}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    )
}

export default DurationPicker
